import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ZulipServices from "../services/APIs/zulipServices";

const apiBaseUrl = import.meta.env.VITE_CITYCOLLAB_API_ROOT_URL;

interface Stream {
  stream_id: number;
  name: string;
}

interface DMUser {
  id: number;
  full_name: string;
  email: string;
  avatar_url: string;
  last_message: string;
  timestamp: number;
}

export const useChatDMChannelList = () => {
  const [channelList, setChannelList] = useState<Stream[]>([]);
  const [dmUserList, setDmUserList] = useState<DMUser[]>([]);
  const currentUserId = parseInt(localStorage.getItem("zulipUserId") ?? "51", 10);

  const getDMUserListFromData = (DMUserList: any[], currentUserId: number) => {
    // Filter only direct/private messages
    const privateMessages = DMUserList?.filter(msg => msg.type === "private");

    // Create a unique map of DM users
    const dmUserMap = new Map();

    privateMessages.forEach(msg => {
      // Each private message has an array of 2 participants
      const participants = msg.display_recipient;

      // Find the "other" user (not the current user)
      const otherUser = participants.find((user: any) => user.id !== currentUserId);

      if (otherUser && !dmUserMap.has(otherUser.id)) {
        dmUserMap.set(otherUser.id, {
          id: otherUser.id,
          full_name: otherUser.full_name,
          email: otherUser.email,
          avatar_url: msg.avatar_url?.startsWith("http")
            ? msg.avatar_url
            : `${apiBaseUrl}${msg.avatar_url}`,
          last_message: msg.content.replace(/<[^>]+>/g, ""), // strip HTML tags
          timestamp: msg.timestamp,
        });
      }
    });

    // Convert to array and sort by latest message
    const dmUserList = Array.from(dmUserMap.values()).sort(
      (a, b) => b.timestamp - a.timestamp
    );

    return dmUserList;
  };

  /**  Fetch Channels (Streams) */
  const {
    data: channelsData,
    isLoading: isLoadingChannels,
    isError: isErrorChannels,
  } = useQuery({
    queryKey: ["zulip", "streams"],
    queryFn: async () => {
      const res = await ZulipServices.getChannelList();
      return res?.streams ?? [];
    },
    staleTime: 1000 * 60 * 2, // cache for 2 min
    retry: 1,
  });

  /**  Update channel state on success */
  useEffect(() => {
    if (channelsData?.length) {
      setChannelList(channelsData);
    }
  }, [channelsData]);

  /** 🔐 DM User List Payload (immutable reference) */
  const DMUserListPayload = useMemo(
    () => ({
      anchor: "newest",
      num_before: 1000,
      num_after: 0,
      client_gravatar: true,
      allow_empty_topic_name: true,
    }),
    []
  );

  /** 📩 Fetch Direct Message User List */
  const {
    data: dmMessages,
    isLoading: isLoadingDMs,
    isError: isErrorDMs,
  } = useQuery({
    queryKey: ["zulip", "dm-users", DMUserListPayload],
    queryFn: async () => {
      const res = await ZulipServices.getDMUserList({ DMUserListPayload });
      return (res as any)?.messages ?? [];
    },
    enabled: !!DMUserListPayload, // only fetch if payload exists
    staleTime: 1000 * 60 * 2,
    retry: 1,
  });

  /** 🧮 Process DM user list */
  useEffect(() => {
    if (Array.isArray(dmMessages) && dmMessages.length > 0) {
      const data = getDMUserListFromData(dmMessages, currentUserId);
      setDmUserList(data as any);
    }
  }, [dmMessages, currentUserId]);

  return {
    channelList,
    dmUserList,
    loading: isLoadingChannels || isLoadingDMs,
    error: isErrorChannels || isErrorDMs,
  };
};
